import { useMemo } from 'react'
import { collection, query, where, documentId } from 'firebase/firestore'
import { db } from '../lib/firebase'
import { useBatchedSubscriptions } from './useBatchedSubscriptions'
import type { ClassifiedError } from '../lib/errors'
import type { Event, EventAttendanceStatus } from '../types'

const EMPTY_EVENTS: Event[] = []

/**
 * Events by id, live, in any region and either side of today — for surfaces
 * tied to a person or a group rather than to discovery. Drafts are dropped the
 * same way `useEvents` drops them, unless an admin page opts in with
 * `includeUnpublished` (the rules only let admins read them).
 *
 * `loading` follows the base-hook convention (nothing to render yet); `pending`
 * is true until every batch has delivered, so callers that freeze a snapshot
 * don't freeze a partial one.
 */
export function useEventsByIds(ids: string[], { includeUnpublished = false } = {}): {
  events: Event[]
  loading: boolean
  pending: boolean
  error: ClassifiedError | null
} {
  const sortedIds = useMemo(() => [...new Set(ids)].sort(), [ids])
  const { rows, loading, pending, error } = useBatchedSubscriptions<Event>(
    sortedIds,
    batch => query(collection(db, 'events'), where(documentId(), 'in', batch)),
    d => ({ id: d.id, ...d.data() }) as Event,
    // Ids can point at draft events (an RSVP or group link to an event that's since unpublished), which the rules deny.
    { deniedIdsExpected: true },
  )
  const events = useMemo(
    () => (rows.length === 0 ? EMPTY_EVENTS : includeUnpublished ? rows : rows.filter(e => e.published !== false)),
    [rows, includeUnpublished],
  )
  return { events, loading, pending, error }
}

/** Ids of the events a person is going to or interested in. */
export function attendingEventIds(statuses: Map<string, EventAttendanceStatus>): string[] {
  const ids: string[] = []
  for (const [id, status] of statuses) {
    if (status === 'going' || status === 'interested') ids.push(id)
  }
  return ids
}
